import { useCallback } from 'react';
import { type Route, type RouteSegments, useNavigation } from 'expo-router';
import { Platform } from 'react-native';

import { useIsNavigationReady } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a type-safe `reset` navigation function for the Expo Router setup.
 */
export const useNavigationReset = () => {
  const isNavigationReady = useIsNavigationReady();
  const navigation = useNavigation('/');

  return useCallback(
    (state: ResetState) => {
      if (!isNavigationReady) return;

      if (Platform.OS === 'web') {
        navigation.reset(state as never);
        return;
      }

      // NOTE: We defer on native to prevent conflicts between
      //       multiple modals.
      requestIdleCallback(() => {
        setTimeout(() => {
          navigation.reset(state as never);
        });
      });
    },
    [isNavigationReady, navigation],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetState = {
  index?: number;
  routes: ResetRoute[];
};

type ResetRoute = {
  name: RootLevelRouteName;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

// NOTE: Nested-navigator screen names are local to their parent stack, so they
//       can't be statically narrowed from the app-wide `Route` union.
type ResetNestedRoute = {
  name: string;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

type RootLevelRouteName = RouteSegments<Route>[number];
